/** @format */

import { type ReactNode, type ChangeEvent, type DragEvent } from 'react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function filterFilesByAccept(files: File[], accept?: string): File[] {
  if (!accept || !files.length) {
    return files;
  }
  const rules = accept.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  return files.filter(file => {
    const name = (file.name || '').toLowerCase();
    const mime = (file.type || '').toLowerCase();
    return rules.some(rule => {
      if (rule.startsWith('.')) {
        return name.endsWith(rule);
      }
      if (rule.endsWith('/*')) {
        const base = rule.slice(0, -2);
        return mime.startsWith(`${base}/`);
      }
      return true;
    });
  });
}

interface FileDropZoneProps {
  accept?: string;
  label?: ReactNode;
  labelSecondary?: ReactNode;
  hint?: string;
  fileName?: string;
  onFile?: (file: File | File[]) => void;
  disabled?: boolean;
  inputId?: string;
  className?: string;
  leading?: ReactNode;
  multiple?: boolean;
}

export default function FileDropZone({
  accept,
  label,
  labelSecondary = '',
  hint = '',
  fileName = '',
  onFile,
  disabled = false,
  inputId,
  className = '',
  leading = null,
  multiple = false
}: FileDropZoneProps) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('contentEditor.dropFileLabel');
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  }, [disabled]);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = Array.from(e.target.files || []);
      const picked = filterFilesByAccept(raw, accept);
      if (picked.length && typeof onFile === 'function') {
        if (multiple) {
          onFile(picked);
        } else {
          onFile(picked[0]);
        }
      }
      e.target.value = '';
    },
    [accept, multiple, onFile]
  );

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      const raw = Array.from(e.dataTransfer.files || []);
      const picked = filterFilesByAccept(raw, accept);
      if (picked.length && typeof onFile === 'function') {
        if (multiple) {
          onFile(picked);
        } else {
          onFile(picked[0]);
        }
      }
    },
    [accept, disabled, multiple, onFile]
  );

  const rootClass = [
    'content-editor__dropzone',
    disabled ? 'content-editor__dropzone--disabled' : '',
    className || ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rootClass}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={openPicker}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      aria-disabled={disabled}>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        className="content-editor__dropzone-input"
        accept={accept}
        disabled={disabled}
        multiple={multiple ? true : undefined}
        onChange={onInputChange}
        aria-hidden
        tabIndex={-1}
      />
      <div
        className={`content-editor__dropzone-inner${leading ? ' content-editor__dropzone-inner--with-leading' : ''}`}>
        {leading ? (
          <div className="content-editor__dropzone-main">
            <span className="content-editor__dropzone-leading">{leading}</span>
            <div className="content-editor__dropzone-text">
              <span className="content-editor__dropzone-label">{resolvedLabel}</span>
              {labelSecondary ? (
                <span className="content-editor__dropzone-label content-editor__dropzone-label--secondary">
                  {labelSecondary}
                </span>
              ) : null}
              {hint ? <span className="content-editor__dropzone-hint">{hint}</span> : null}
            </div>
          </div>
        ) : (
          <>
            <span className="content-editor__dropzone-label">{resolvedLabel}</span>
            {hint ? <span className="content-editor__dropzone-hint">{hint}</span> : null}
          </>
        )}
        {fileName ? <span className="content-editor__dropzone-file">{fileName}</span> : null}
      </div>
    </div>
  );
}
